/**
 * Client-side shapes for the Domain Pack judgment surfaces (DC-1518).
 *
 * Mirrors, never invents: `PackJudgmentSummary` from
 * server/contextualValidity/packAssessment.ts (the `pack_judgment` field on
 * the CV run responses) and the GET /api/cv-judgments/:id read-side
 * (server/routes/cvJudgmentRoutes.ts). The outcome vocabulary is locked to
 * the mig-128 CHECK constraint — exactly four values.
 *
 * UX spec: docs/design/v3.0-domain-pack-surfaces-ux-spec.md.
 */

/** Mirrors chk_cv_judgment_rule_results_status (migration 128). */
export const RULE_OUTCOMES = ['pass', 'fail', 'evidence_missing', 'not_applicable'] as const;
export type RuleOutcome = (typeof RULE_OUTCOMES)[number];

export function isRuleOutcome(value: string): value is RuleOutcome {
  return (RULE_OUTCOMES as readonly string[]).includes(value);
}

/**
 * Scanning priority for the card list — worst first (spec §2.2), consistent
 * with every other Compass findings surface. Id-ascending within a group.
 */
export const OUTCOME_SORT_ORDER: Record<RuleOutcome, number> = {
  fail: 0,
  evidence_missing: 1,
  pass: 2,
  not_applicable: 3,
};

export interface SummaryEvidence {
  source: string;
  target: string;
  observation: unknown;
  confidence: number;
}

export interface RuleRemediation {
  action: string;
  estimated_effort: string;
  estimated_value: string;
}

export interface RegulatoryCitation {
  framework: string;
  reference: string;
  section?: string;
}

/** One rule outcome as the CV run response carries it (full projection). */
export interface SummaryRuleResult {
  rule_id: string;
  rule_version: string;
  outcome: string;
  evidence_count: number;
  title?: string;
  severity?: string;
  description?: string;
  evidence: SummaryEvidence[];
  remediation?: RuleRemediation;
  regulatory_citations: RegulatoryCitation[];
}

export interface ArchetypeCoverageEntry {
  archetype_id: string;
  coverage: number;
}

export type PackSource = 'domain-binding' | 'perspective-fallback';
export type SynthesisSkippedReason = 'no-llm-provider' | 'no-dataset';

/** The `pack_judgment` field on generate-fit / analyze-from-profile responses. */
export interface PackJudgmentSummary {
  judgment_id: string | null;
  pack_ref: string;
  pack_source: PackSource;
  input_digest: string;
  rule_results: SummaryRuleResult[];
  archetype_coverage: ArchetypeCoverageEntry[];
  classification: string | null;
  confidence: number | null;
  narrative: string | null;
  synthesis_skipped_reason?: SynthesisSkippedReason;
}

/** One append-only override row (mig 129) as the read-side returns it. */
export interface JudgmentOverride {
  id: string;
  judgment_id: string;
  original_classification: string;
  new_classification: string;
  reason: string;
  created_at: string;
  created_by?: string;
}

/** GET /api/cv-judgments/:id response (pack-enriched, fail-soft). */
export interface JudgmentDetailResponse {
  judgment: {
    id: string;
    classification: string;
    confidence: number;
    narrative: string;
    pack_ref: string;
    input_digest: string;
    created_at: string;
    pack_resolved: boolean;
  };
  pack_subdomains: Array<{ id: string; caption: string }> | null;
  overrides: JudgmentOverride[];
  overridden: boolean;
  effective_classification: string;
}

/** GET /api/contextual-validity/journeys/:journeyId/pack-resolution response. */
export interface PackResolution {
  bound: boolean;
  pack_ref: string;
  pack_source: PackSource;
  domain_name: string | null;
}
